import { WithScope } from '@mews-commander/types';
import { TextKey } from '@mews-commander/enums';
import { useCommanderSelector } from '@mews-commander/utils';
import { getServicePerTimeUnitLocalization } from '@mews-commander/legacy-entities/src/utils/serviceTimeUnitLocalization';
import { DropdownItem, IconName } from '@optimus-web/core';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import {
    createZodFormResolver,
    FormInputField,
    FormSelectField,
    lt,
    NumberService,
    SubmitHandler,
    useFormControls,
} from '@mews/framework';
import { TooltippedIcon } from '@mews-commander/ui';
import { getCurrentCurrencyCultureInfo } from '@mews-commander/core/legacy-globalization';
import { FormLayout } from '../../FormLayout';
import { getRateFormServiceId } from '../selectors';
import { getAverageNightRateInitialValues } from '../../../selectors/pricing';
import { AverageNightRateFormKeys, averageNightRateFormSchema, AverageNightRateFormValues } from './types';
import { useAverageNightRateFormMutation } from './useAverageNightRateFormMutation';

interface AverageNightRateFormContainerProps extends WithScope {
    reservationIds: string[];
    onSuccess?: () => void;
}

const FORM_PREFIX = 'average-night-rate-form-';

export const AverageNightRateFormContainer = ({
    scope,
    reservationIds,
    onSuccess,
}: AverageNightRateFormContainerProps) => {
    const { acceptedCurrencyCodes, defaultCurrencyCode } = useCurrentEnterprise();
    const serviceId = useCommanderSelector((state) => getRateFormServiceId(state, { scope })) ?? '';
    const localizationInfo = useCommanderSelector(getCurrentCurrencyCultureInfo);
    const timeUnitText = useCommanderSelector((state) => getServicePerTimeUnitLocalization(state, { id: serviceId }));

    const defaultValues = useCommanderSelector((state) =>
        getAverageNightRateInitialValues(state, { serviceId, serviceOrderIds: reservationIds })
    );

    const { mutate, isPending } = useAverageNightRateFormMutation();
    const onSubmit: SubmitHandler<AverageNightRateFormValues> = ({ currency, reason, value }) => {
        const unformattedValue = NumberService.unformat(value, localizationInfo);

        mutate(
            {
                reservationIds,
                reason,
                amount: { value: unformattedValue, currencyCode: currency },
            },
            {
                onSuccess: () => {
                    // Reset only the reason field
                    onSuccess?.();
                },
            }
        );
    };

    // Creating the form control
    const { renderFormContent } = useFormControls<AverageNightRateFormValues>({
        onSubmit,
        resolver: createZodFormResolver(averageNightRateFormSchema(localizationInfo)),
        values: {
            [AverageNightRateFormKeys.value]: defaultValues.value ?? '',
            [AverageNightRateFormKeys.currency]: defaultValues.currency ?? defaultCurrencyCode,
            [AverageNightRateFormKeys.reason]: '',
        },
        dataTestForm: `${FORM_PREFIX}${scope}`,
    });

    return renderFormContent(
        <AverageNightRateForm
            isLoading={isPending}
            timeUnitText={timeUnitText}
            acceptedCurrencyCodes={acceptedCurrencyCodes || []}
        />
    );
};

interface AverageNightRateFormProps {
    isLoading: boolean;
    timeUnitText: string;
    acceptedCurrencyCodes: string[];
}

const AverageNightRateForm = ({ isLoading, timeUnitText, acceptedCurrencyCodes }: AverageNightRateFormProps) => {
    return (
        <FormLayout
            title={lt('SetAverageTimeUnitRateExcludingProducts', {
                timeUnit: timeUnitText,
            })}
            loading={isLoading}
            submitButtonLoading={isLoading}
        >
            <FormInputField
                name={AverageNightRateFormKeys.value}
                required
                label={lt('AverageTimeUnitRateShort', {
                    timeUnit: timeUnitText,
                })}
                captionIconWithTooltip={
                    <TooltippedIcon
                        size="small"
                        value={IconName.Info}
                        tooltip={TextKey.AverageNightRateFormValueTooltip}
                    />
                }
            />
            <FormSelectField name={AverageNightRateFormKeys.currency} label={lt('Currency')} required>
                {(acceptedCurrencyCodes || []).map((item) => (
                    <DropdownItem key={item} value={item} label={item} />
                ))}
            </FormSelectField>
            <FormInputField
                name={AverageNightRateFormKeys.reason}
                label={lt('ReservationDetailReasonInputLabel')}
                required
            />
        </FormLayout>
    );
};
